import { appConfig } from '../lib/config';
import { DATA_LOW_WATER, PROTOCOL_VERSION } from '../lib/constants';
import type { SignalPayload, SignalingSession } from '../signaling/signaling';

export interface PeerDiagnostics {
  connectionState: RTCPeerConnectionState;
  iceState: RTCIceConnectionState;
  signalingState: RTCSignalingState;
  candidateType?: string;
  protocol?: string;
  roundTripTimeMs?: number;
}

export interface PeerEvents {
  onReady: () => void;
  onState: (diagnostics: PeerDiagnostics) => void;
  onError: (error: Error) => void;
}

export class PeerSession {
  private connection: RTCPeerConnection;
  private control?: RTCDataChannel;
  private data?: RTCDataChannel;
  private controlListeners = new Set<(value: unknown) => void>();
  private dataListeners = new Set<(buffer: ArrayBuffer) => void>();
  private readyNotified = false;
  private candidateType?: string;
  private protocol?: string;
  private roundTripTimeMs?: number;
  private statsTimer?: number;
  readonly role: 'host' | 'guest';
  readonly signaling: SignalingSession;
  readonly remoteUid: string;
  readonly events: PeerEvents;

  constructor(role: 'host' | 'guest', signaling: SignalingSession, remoteUid: string, events: PeerEvents) {
    this.role = role;
    this.signaling = signaling;
    this.remoteUid = remoteUid;
    this.events = events;
    this.connection = new RTCPeerConnection({
      iceServers: [{ urls: appConfig.stunUrls }],
      bundlePolicy: 'max-bundle',
    });
    this.connection.onconnectionstatechange = () => void this.handleConnectionState();
    this.connection.oniceconnectionstatechange = () => this.emitState();
    this.connection.onsignalingstatechange = () => this.emitState();
    if (role === 'host') {
      this.attachControl(this.connection.createDataChannel('control', { ordered: true }));
      this.attachData(this.connection.createDataChannel('data', { ordered: true }));
    } else {
      this.connection.ondatachannel = (event) => {
        if (event.channel.label === 'control') this.attachControl(event.channel);
        if (event.channel.label === 'data') this.attachData(event.channel);
      };
    }
  }

  async start(): Promise<void> {
    this.signaling.listenForSignals((signal) => void this.receiveSignal(signal));
    if (this.role === 'host') {
      await this.connection.setLocalDescription(await this.connection.createOffer());
      await this.waitForIceGathering();
      await this.signaling.sendSignal(this.remoteUid, { kind: 'description', description: this.connection.localDescription! });
    }
  }

  private async receiveSignal(signal: SignalPayload): Promise<void> {
    try {
      if (signal.kind === 'candidate') {
        await this.connection.addIceCandidate(signal.candidate);
        return;
      }
      await this.connection.setRemoteDescription(signal.description);
      if (signal.description.type === 'offer') {
        await this.connection.setLocalDescription(await this.connection.createAnswer());
        await this.waitForIceGathering();
        await this.signaling.sendSignal(this.remoteUid, { kind: 'description', description: this.connection.localDescription! });
      }
    } catch (error) {
      this.events.onError(error instanceof Error ? error : new Error('WebRTC negotiation failed.'));
    }
  }

  private waitForIceGathering(timeoutMs = 12_000): Promise<void> {
    if (this.connection.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const timeout = window.setTimeout(done, timeoutMs);
      const listener = () => {
        if (this.connection.iceGatheringState === 'complete') done();
      };
      const connection = this.connection;
      connection.addEventListener('icegatheringstatechange', listener);
      function done() {
        window.clearTimeout(timeout);
        connection.removeEventListener('icegatheringstatechange', listener);
        resolve();
      }
    });
  }

  private attachControl(channel: RTCDataChannel): void {
    this.control = channel;
    channel.onopen = () => this.maybeReady();
    channel.onmessage = (event) => {
      try {
        const value = JSON.parse(String(event.data)) as { protocolVersion?: number };
        if (value.protocolVersion !== undefined && value.protocolVersion !== PROTOCOL_VERSION) throw new Error('Protocol version mismatch.');
        this.controlListeners.forEach((listener) => listener(value));
      } catch (error) {
        this.events.onError(error instanceof Error ? error : new Error('Invalid control message.'));
      }
    };
    channel.onerror = () => this.events.onError(new Error('The WebRTC control channel failed.'));
  }

  private attachData(channel: RTCDataChannel): void {
    this.data = channel;
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = DATA_LOW_WATER;
    channel.onopen = () => this.maybeReady();
    channel.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) this.dataListeners.forEach((listener) => listener(event.data));
    };
    channel.onerror = () => this.events.onError(new Error('The WebRTC file channel failed.'));
  }

  private maybeReady(): void {
    if (this.readyNotified || this.control?.readyState !== 'open' || this.data?.readyState !== 'open') return;
    this.readyNotified = true;
    this.events.onReady();
  }

  private async handleConnectionState(): Promise<void> {
    if (this.connection.connectionState === 'connected') {
      try {
        await this.refreshConnectionStats();
        if (!this.statsTimer) {
          this.statsTimer = window.setInterval(() => {
            void this.refreshConnectionStats().then(() => this.emitState()).catch(() => undefined);
          }, 5_000);
        }
      } catch (error) {
        this.events.onError(error instanceof Error ? error : new Error('Could not inspect the direct connection.'));
        return;
      }
    } else if (this.statsTimer) {
      window.clearInterval(this.statsTimer);
      this.statsTimer = undefined;
    }
    this.emitState();
    if (this.connection.connectionState === 'failed') {
      this.events.onError(new Error('A direct connection could not be established. Disable VPNs or try another network or hotspot.'));
    }
  }

  private async refreshConnectionStats(): Promise<void> {
    const reports = await this.connection.getStats();
    let localCandidateId: string | undefined;
    let selectedPair: RTCStats | undefined;
    reports.forEach((report) => {
      if (report.type === 'candidate-pair' && report.state === 'succeeded' && (report.nominated || !localCandidateId)) {
        localCandidateId = report.localCandidateId as string;
        selectedPair = report;
      }
    });
    const candidate = localCandidateId ? reports.get(localCandidateId) : undefined;
    const type = candidate?.candidateType as string | undefined;
    if (type === 'relay') {
      this.close();
      throw new Error('A relay connection was rejected because this deployment is direct-only.');
    }
    this.candidateType = type;
    this.protocol = candidate?.protocol as string | undefined;
    const roundTripTime = (selectedPair as (RTCStats & { currentRoundTripTime?: number }) | undefined)?.currentRoundTripTime;
    this.roundTripTimeMs = typeof roundTripTime === 'number' ? Math.max(0, Math.round(roundTripTime * 1_000)) : undefined;
  }

  private emitState(): void {
    this.events.onState(this.diagnostics());
  }

  diagnostics(): PeerDiagnostics {
    return {
      connectionState: this.connection.connectionState,
      iceState: this.connection.iceConnectionState,
      signalingState: this.connection.signalingState,
      candidateType: this.candidateType,
      protocol: this.protocol,
      roundTripTimeMs: this.roundTripTimeMs,
    };
  }

  onControl(listener: (value: unknown) => void): () => void {
    this.controlListeners.add(listener);
    return () => this.controlListeners.delete(listener);
  }

  onData(listener: (buffer: ArrayBuffer) => void): () => void {
    this.dataListeners.add(listener);
    return () => this.dataListeners.delete(listener);
  }

  sendControl(value: unknown): void {
    if (this.control?.readyState !== 'open') throw new Error('The direct connection is not ready.');
    this.control.send(JSON.stringify(value));
  }

  sendData(buffer: ArrayBuffer): void {
    if (this.data?.readyState !== 'open') throw new Error('The direct file channel is not ready.');
    this.data.send(buffer);
  }

  get bufferedAmount(): number {
    return this.data?.bufferedAmount ?? 0;
  }

  waitForDataBuffer(): Promise<void> {
    if (!this.data || this.data.bufferedAmount <= DATA_LOW_WATER) return Promise.resolve();
    return new Promise((resolve) => this.data!.addEventListener('bufferedamountlow', () => resolve(), { once: true }));
  }

  close(): void {
    if (this.statsTimer) window.clearInterval(this.statsTimer);
    this.statsTimer = undefined;
    this.control?.close();
    this.data?.close();
    this.connection.close();
    this.emitState();
  }
}
